import React from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Moon, X } from 'lucide-react';
import { motion, AnimatePresence, useDragControls } from 'framer-motion';
import ProgressivePromptCard from './ProgressivePromptCard';

const ProgressiveModal = ({ isOpen, onClose, question, onSave, onSkip }) => {
  const { t } = useTranslation();
  const dragControls = useDragControls();
  if (!isOpen) return null;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[200] flex items-end sm:items-center justify-center"
          onClick={onClose}
        >
          <motion.div
            initial={{ y: '100%', opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: '100%', opacity: 0 }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            drag="y"
            dragControls={dragControls}
            dragListener={false}
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0, bottom: 0.5 }}
            onDragEnd={(_, info) => { if (info.offset.y > 100) onClose(); }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-t-2xl sm:rounded-2xl overflow-hidden flex flex-col"
            style={{ background: 'var(--c-surface-elevated)', height: 'calc(100dvh - 24px)', maxHeight: 'calc(100dvh - 24px)' }}
          >
            {/* Drag handle */}
            <div
              className="sm:hidden flex justify-center pt-3 pb-1 cursor-grab active:cursor-grabbing"
              onPointerDown={(e) => dragControls.start(e)}
              style={{ touchAction: 'none' }}
            >
              <div className="w-10 h-1 rounded-full" style={{ background: 'var(--c-border)' }} />
            </div>
            <div className="p-4 flex items-center justify-between" style={{ borderBottom: '1px solid var(--c-border)' }}>
              <div className="flex items-center gap-2">
                <Moon className="w-5 h-5" style={{ color: '#818cf8' }} />
                <h2 className="font-semibold" style={{ color: 'var(--c-texPri)' }}>{t('progressive.modalTitle', 'Quick question')}</h2>
              </div>
              <button
                onClick={onClose}
                className="w-8 h-8 rounded-full flex items-center justify-center transition-colors"
                style={{ background: 'var(--c-surface-card)' }}
              >
                <X className="w-4 h-4" style={{ color: 'var(--c-texSec)' }} />
              </button>
            </div>

            <div className="p-6 overflow-y-auto flex-1">
              {question ? (
                <ProgressivePromptCard question={question} onSave={(k, v) => { onSave(k, v); }} onSkip={() => { onSkip(); }} />
              ) : (
                <div className="p-6 text-center" style={{ color: 'var(--c-texTer)' }}>No question</div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default ProgressiveModal;
